import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  TablePagination,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Button,
  Box,
  InputAdornment,
} from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useDownloadEvaluation } from "../../Query/useEvaluation";
import { getEvaluationHistory } from "../../Api/evaluations";
import { downloadDocumentBlob } from "../../Helpers/documentHelper";
import colors from "../../Constants/colors";
import {
  EvaluationHistory as EvaluationHistoryType,
  FileData,
} from "../../Models/evaluation";

interface EvaluationHistoryProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
}

const EvaluationHistory: React.FC<EvaluationHistoryProps> = ({
  open,
  onClose,
  companyId,
}) => {
  const [evaluationData, setEvaluationData] =
    useState<EvaluationHistoryType | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sortField, setSortField] = useState<string>("created_at");
  const navigate = useNavigate();
  const { t } = useTranslation("singleCompanyComponent");
  const downloadEvaluationMutation = useDownloadEvaluation();

  const fetchEvaluationHistory = useCallback(
    async (
      page: number,
      rowsPerPage: number,
      search: string,
      sort: "asc" | "desc",
      sortField: string,
    ) => {
      try {
        const response = await getEvaluationHistory(
          companyId,
          page,
          rowsPerPage,
          search,
          sortField,
          sort,
        );
        setEvaluationData(response);
      } catch (error) {
        console.error(
          t("evaluationHistory.errorFetchingEvaluationHistory"),
          error,
        );
      }
    },
    [companyId, t],
  );

  useEffect(() => {
    if (open) {
      fetchEvaluationHistory(page, rowsPerPage, search, sortOrder, sortField);
    }
  }, [
    open,
    page,
    rowsPerPage,
    search,
    sortOrder,
    sortField,
    fetchEvaluationHistory,
  ]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchEvaluationHistory(newPage, rowsPerPage, search, sortOrder, sortField);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleSortChange = (field: string, order: "asc" | "desc") => {
    setSortField(field);
    setSortOrder(order);
    fetchEvaluationHistory(page, rowsPerPage, search, order, field);
  };

  const handleDownload = async (
    name: string,
    user_evaluation_request_id: string,
  ) => {
    downloadEvaluationMutation.mutate(user_evaluation_request_id, {
      onSuccess: (response) => {
        downloadDocumentBlob(response, name);
      },
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            margin: 2,
            width: "85%",
            maxWidth: "1100px",
            borderRadius: "8px",
            border: `1px solid ${colors.gray200}`,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderBottom: `1px solid ${colors.gray200}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: colors.primary_dark, fontWeight: 600 }}
          >
            {t("evaluationHistory.evaluationHistoryTitle")}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              padding: "8px",
              borderRadius: "4px",
              "&:hover": { backgroundColor: colors.gray100 },
            }}
          >
            <CloseIcon sx={{ color: colors.gray500 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "20px 24px 12px 24px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              marginTop: "8px",
            }}
          >
            <TextField
              placeholder={t("evaluationHistory.search")}
              fullWidth
              variant="outlined"
              value={search}
              size="small"
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors.gray500 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: "420px",
                "& .MuiOutlinedInput-root": {
                  height: 42,
                  borderRadius: "10px",
                  backgroundColor: colors.gray50,
                  "& fieldset": { borderColor: colors.gray200 },
                  "&:hover fieldset": { borderColor: colors.gray300 },
                  "&.Mui-focused fieldset": { borderColor: colors.accent },
                  "& input::placeholder": { color: colors.gray500 },
                  "& input": { color: colors.primary_dark },
                },
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ color: colors.gray600 }}>
                {t("evaluationHistory.evaluatedAt")}
              </Typography>
              <IconButton
                sx={{
                  color: colors.accent,
                  backgroundColor: colors.gray50,
                  border: `1px solid ${colors.gray200}`,
                  "&:hover": { backgroundColor: colors.orange50 },
                }}
                onClick={() =>
                  handleSortChange(
                    sortField,
                    sortOrder === "asc" ? "desc" : "asc",
                  )
                }
              >
                {sortOrder === "asc" ? <ArrowUpward /> : <ArrowDownward />}
              </IconButton>
            </Box>
          </Box>

          {evaluationData ? (
            evaluationData.files.length > 0 ? (
              <Box
                sx={{
                  marginTop: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {evaluationData.files.map((file: FileData) => (
                  <Box
                    key={file.user_evaluation_request_id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      padding: "12px 16px",
                      borderRadius: "8px",
                      border: `1px solid ${colors.gray200}`,
                      backgroundColor: colors.white,
                      "&:hover": {
                        borderColor: colors.accent,
                        backgroundColor: `${colors.accent}05`,
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.primary_dark,
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "520px",
                        }}
                        title={file.name}
                      >
                        {file.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.gray600,
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        {t("evaluationHistory.evaluatedAt")}: {file.created_at}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="caption"
                          sx={{ color: colors.gray500 }}
                        >
                          {t("evaluationHistory.grade")}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: colors.accent, fontWeight: 600 }}
                        >
                          {file.grade}
                        </Typography>
                      </Box>
                      <Tooltip
                        title={t("evaluationHistory.downloadEvaluation")}
                      >
                        <IconButton
                          sx={{
                            color: colors.accent,
                            backgroundColor: "transparent",
                            "&:hover": { backgroundColor: "transparent" },
                          }}
                          onClick={() => {
                            handleDownload(
                              file.name,
                              file.user_evaluation_request_id,
                            );
                          }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
                <TablePagination
                  rowsPerPageOptions={[]}
                  component="div"
                  count={evaluationData.total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  sx={{
                    color: colors.primary_dark,
                    "& .MuiTablePagination-displayedRows": {
                      color: colors.primary_dark,
                    },
                    "& .MuiTablePagination-toolbar": {
                      color: colors.primary_dark,
                    },
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "48px 24px",
                  marginTop: 2,
                  borderRadius: "8px",
                  border: `1px dashed ${colors.gray300}`,
                  backgroundColor: colors.gray50,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.gray600,
                    textAlign: "center",
                  }}
                >
                  {t("evaluationHistory.noEvaluationsMessage")}
                </Typography>
                <Button
                  sx={{
                    backgroundColor: colors.accent,
                    marginTop: "16px",
                    color: colors.white,
                    "&:hover": {
                      backgroundColor: colors.accent_dark,
                      color: colors.white,
                    },
                  }}
                  onClick={() => navigate("/evaluation")}
                >
                  {t("evaluationHistory.startEvaluation")}
                </Button>
              </Box>
            )
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress sx={{ color: colors.primary_dark }} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EvaluationHistory;
